import type { GoogleTagManagerApi } from '#nuxt-scripts/registry/google-tag-manager'
import type { RouteLocationNormalized, Router } from 'vue-router'
import type { DeferredAppInsights } from '~/utils/deferredAppInsights'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

// One page-view signal per navigation, first load included, to GTM and App
// Insights. nuxt2 got these from @nuxtjs/gtm `pageTracking` and from App
// Insights `enableAutoRouteTracking`; nuxt4 had neither. AB#64563
//
// App Insights is tracked from the router, not with enableAutoRouteTracking:
// the SDK records a page view on every history.pushState AND replaceState, and
// vue-router 5 calls both on each navigation (replaceState saves the scroll
// position first), so the flag counts every navigation twice. nuxt2's
// vue-router 3 made a single call, so this keeps nuxt2's one-per-navigation.
// maxAjaxCallsPerView needs no config: nuxt2 set the SDK default (500), and
// trackPageView resets that per-view count as the route tracking did.

// The delays each source used in nuxt2, so the document title has settled.
export const GTM_PAGE_VIEW_DELAY = 250 // @nuxtjs/gtm startPageTracking
export const APP_INSIGHTS_PAGE_VIEW_DELAY = 500 // SDK autoRoutePVDelay default

type PageViewTargets = {
  gtm?: GoogleTagManagerApi
  appInsights?: Pick<DeferredAppInsights, 'trackPageView' | 'withCore'>
}

// What the SDK's route tracking did on each route change: a new trace id, so a
// page's requests are grouped under its own operation rather than the first
// page's. The first view keeps whatever context the SDK already has: nothing hands
// the SSR request's trace to the browser SDK, so it is not tied to the server request.
// Before the deferred SDK has loaded, withCore holds the reset and applies it at
// replay, ahead of that page's page view.

// crypto.randomUUID is absent on older Safari and in any non-secure context. A throw
// here would abort the router.afterEach hook, and with it every later page view, while
// the GTM push (scheduled first) kept working, so the loss would be silent. The same
// guard as useSearchResultTracking's newSearchId. AB#63268
const newTraceId = () => {
  try {
    return crypto.randomUUID().replaceAll('-', '')
  } catch {
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
  }
}

const startNewTrace = (appInsights: PageViewTargets['appInsights']) => {
  // Generated now, so a reset held until the SDK loads still gets its own id.
  const traceId = newTraceId()
  appInsights?.withCore((core) => {
    const traceCtx = core?.getTraceCtx?.(true)
    if (core && traceCtx) {
      // A W3C trace id is 32 lowercase hex characters. The SDK's generateW3CId
      // is not exported from @microsoft/applicationinsights-web.
      traceCtx.traceId = traceId
      core.setTraceCtx(traceCtx)
    }
  })
}

// nuxt2 named the /fr and /de copies of each route `lang-<name>` (makeLangRoutes),
// so a page view there reported e.g. `lang-products-slug`. nuxt4 keeps the plain
// name on those routes and marks them `langPrefixed` instead.
export const gtmRouteName = (to: RouteLocationNormalized) =>
  to.meta.langPrefixed && to.name ? `lang-${String(to.name)}` : to.name

export const trackPageViews =(router: Router, { gtm, appInsights }: PageViewTargets) => {
  // undefined on the first view, so the SDK reports document.referrer, as it did in nuxt2
  let previousUrl: string | undefined

  router.afterEach((to, _from, failure) => {
    // An aborted, cancelled or duplicate navigation never showed a page
    if (failure) {
      return
    }

    setTimeout(() => {
      // nuxt2's payload; its router base was '/', so pageUrl is the full path
      gtm?.dataLayer.push({
        event: 'nuxtRoute',
        pageType: 'PageView',
        routeName: gtmRouteName(to),
        pageUrl: to.fullPath,
        pageTitle: document.title || ''
      })
    }, GTM_PAGE_VIEW_DELAY)

    const refUri = previousUrl
    // New trace now, page view after the delay, as the SDK's route tracking did: a
    // navigation inside the delay reports the earlier view under the later trace
    if (refUri !== undefined) {
      startNewTrace(appInsights)
    }
    previousUrl = window.location.href
    // Named after the route, as nuxt-applicationinsights' initializer did
    const name = to.name ? String(to.name) : to.path
    setTimeout(() => {
      appInsights?.trackPageView({ name, refUri, properties: { duration: 0 } })
    }, APP_INSIGHTS_PAGE_VIEW_DELAY)
  })
}

export default defineNuxtPlugin({
  name: 'page-view-tracking',
  // GTM is started by third-party-tags; this reuses that instance
  dependsOn: ['third-party-tags'],
  setup (nuxtApp) {
    const { GTM_ID } = useTypedRuntimeConfig().public
    trackPageViews(useRouter(), {
      gtm: GTM_ID ? useScriptGoogleTagManager().proxy : undefined,
      appInsights: nuxtApp.$appInsights
    })
  }
})
